// Schema introspection (registry editing). PURE (ENG-1).
//
// Turns a zod schema into a flat description of its fields so a UI can build
// itself: a bounded number becomes a slider, an enum becomes a dropdown, a
// boolean a checkbox. The point is that the editor never hardcodes the item or
// mob shape — it reads the same schemas the loader validates against, so a field
// added to schemas.ts shows up in the editor with no UI change at all.
import { z } from 'zod';

export type FieldKind = 'string' | 'number' | 'boolean' | 'enum' | 'array' | 'object' | 'unknown';

export interface FieldDesc {
  key: string;
  kind: FieldKind;
  /** Omitted values are legal (the loader fills a default or leaves it unset). */
  optional: boolean;
  /** Schema default, when the field declares one. */
  fallback?: unknown;
  /** Allowed values — render as a dropdown. */
  values?: readonly string[];
  /** Numeric bounds; with both present a slider is exact rather than a guess. */
  min?: number;
  max?: number;
  /** Whole numbers only (slider step 1). */
  int?: boolean;
  /** Nested shape, for an object field. */
  fields?: FieldDesc[];
  /** Element description, for an array field. */
  element?: FieldDesc;
  /** The schema's own doc text, when one was supplied. */
  description?: string;
}

/** The slice of zod's internal node shape this module reads. Zod does not export
 *  these, and the public API cannot describe a schema, so the structure is named
 *  here rather than smeared as `any` across the file. */
interface ZodNode {
  def?: {
    type?: string;
    innerType?: ZodNode;
    defaultValue?: unknown;
    entries?: Record<string, unknown>;
    element?: ZodNode;
    shape?: Record<string, ZodNode>;
    options?: ZodNode[];
    checks?: ZodCheck[];
    values?: unknown[];
    value?: unknown;
  };
  description?: string;
}
interface ZodCheck {
  _zod?: { def?: ZodCheckDef };
  def?: ZodCheckDef;
  check?: string;
  format?: string;
  value?: number;
  inclusive?: boolean;
}
interface ZodCheckDef {
  check?: string;
  format?: string;
  value?: number;
  inclusive?: boolean;
}

/** Peel `.optional()` / `.default()` / `.nullable()` wrappers off a schema. */
function unwrap(schema: unknown): { inner: ZodNode | undefined; optional: boolean; fallback?: unknown } {
  let cur = schema as ZodNode | undefined;
  let optional = false;
  let fallback: unknown;
  // Wrappers can nest in either order, so loop rather than checking once.
  for (let guard = 0; guard < 8 && cur?.def; guard++) {
    const t = cur.def.type;
    if (t === 'optional' || t === 'nullable') { optional = true; cur = cur.def.innerType; continue; }
    if (t === 'default' || t === 'prefault') {
      fallback = typeof cur.def.defaultValue === 'function' ? cur.def.defaultValue() : cur.def.defaultValue;
      cur = cur.def.innerType;
      continue;
    }
    break;
  }
  return { inner: cur, optional, fallback };
}

/** Numeric bounds + integer-ness from a number schema's checks. */
function numberBounds(inner: ZodNode | undefined): { min?: number; max?: number; int?: boolean } {
  const out: { min?: number; max?: number; int?: boolean } = {};
  for (const c of inner?.def?.checks ?? []) {
    const d: ZodCheckDef = c?._zod?.def ?? c?.def ?? c;
    if (d?.format === 'safeint' || d?.format === 'int') out.int = true;
    if (d?.check === 'greater_than' && d.value !== undefined) out.min = d.inclusive ? d.value : d.value + (out.int ? 1 : 0);
    if (d?.check === 'less_than' && d.value !== undefined) out.max = d.inclusive ? d.value : d.value - (out.int ? 1 : 0);
  }
  return out;
}

function describe(key: string, schema: unknown): FieldDesc {
  const { inner, optional, fallback } = unwrap(schema);
  const type = inner?.def?.type;
  const description = inner?.description ?? (schema as ZodNode | undefined)?.description;
  const base: FieldDesc = { key, kind: 'unknown', optional, description };
  if (fallback !== undefined) base.fallback = fallback;

  switch (type) {
    case 'string': return { ...base, kind: 'string' };
    case 'boolean': return { ...base, kind: 'boolean' };
    case 'number': return { ...base, kind: 'number', ...numberBounds(inner) };
    case 'enum': return { ...base, kind: 'enum', values: Object.values(inner?.def?.entries ?? {}).map(String) };
    case 'array': return { ...base, kind: 'array', element: describe('', inner?.def?.element) };
    case 'object': return { ...base, kind: 'object', fields: describeShape(inner) };
    // A union of literals is a dropdown too; anything else stays raw JSON.
    case 'union': {
      const opts = inner?.def?.options ?? [];
      const literals = opts
        .filter((o) => o?.def?.type === 'literal')
        .map((o) => o.def?.values?.[0] ?? o.def?.value);
      if (literals.length === opts.length && literals.length > 0) {
        return { ...base, kind: 'enum', values: literals.map(String) };
      }
      return base;
    }
    default: return base;
  }
}

/** Field descriptions for an object schema, in declaration order. */
export function describeShape(schema: unknown): FieldDesc[] {
  const { inner } = unwrap(schema);
  const shape = inner?.def?.shape;
  if (!shape) return [];
  return Object.entries(shape).map(([key, sub]) => describe(key, sub));
}

/** Convenience: describe a top-level registry schema (item, mob, block…). */
export function describeSchema(schema: z.ZodType): FieldDesc[] {
  return describeShape(schema);
}

/** A field is slider-friendly when it is numeric and bounded at both ends. */
export function isSlider(f: FieldDesc): boolean {
  return f.kind === 'number' && f.min !== undefined && f.max !== undefined;
}
